import { type FC } from "react";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";

import { AppStrings } from "../../../../utils/constants/AppStrings";
import { Form, Input, Select } from "../../../shared";
import useCurrencySymbol from "../../../../hooks/useCurrencySymbol";

import type { RevenueSource } from "./Payment";

import styles from "./RevShareConditions.module.css";

const showsSportsbook = (source?: RevenueSource): boolean =>
  source === "sportsbook_casino" || source === "sportsbook";

const showsCasino = (source?: RevenueSource): boolean =>
  source === "sportsbook_casino" || source === "casino";

type ProfitRowProps = {
  scope: "sportsbook" | "casino";
  profitLabel: string;
  profitTestId: string;
  pctTestId: string;
};

const REVENUE_SOURCE_OPTIONS: ReadonlyArray<{
  value: RevenueSource;
  label: string;
}> = [
  {
    value: "sportsbook_casino",
    label: AppStrings.SPORTSBOOK_CASINO_REVENUE,
  },
  { value: "sportsbook", label: AppStrings.SPORTSBOOK_REVENUE },
  { value: "casino", label: AppStrings.CASINO_REVENUE },
];

const ProfitRow: FC<ProfitRowProps> = ({
  scope,
  profitLabel,
  profitTestId,
  pctTestId,
}) => {
  const currencySymbol = useCurrencySymbol() ?? "$";
  return (
  <div className={styles.row}>
    <Form.Item
      name={["revShare", scope, "minProfit"]}
      label={
        <span className={styles.labelWithTooltip}>
          {`${profitLabel}*`}
          <Tooltip title={AppStrings.MINIMUM_MONTHLY_PROFIT_THRESHOLD}>
            <InfoCircleOutlined
              className={styles.labelInfoIcon}
              aria-label={AppStrings.ABOUT_MINIMUM_PROFIT}
            />
          </Tooltip>
        </span>
      }
      rules={[
        { required: true, message: AppStrings.REQUIRED },
        Form.validators.numericRange({
          min: 0.01,
          message: AppStrings.MUST_BE_GREATER_THAN_0,
        }),
      ]}
    >
      <Input
        prefixIcon={
          <span className={styles.adornment}>{currencySymbol}</span>
        }
        placeholder={"0"}
        inputMode="decimal"
        data-testid={profitTestId}
      />
    </Form.Item>

    <Form.Item
      name={["revShare", scope, "revenuePct"]}
      label={`${AppStrings.REVENUE_PCT}*`}
      rules={[
        { required: true, message: AppStrings.REQUIRED },
        Form.validators.numericRange({
          integer: true,
          message: AppStrings.MUST_BE_WHOLE_NUMBER,
        }),
        Form.validators.numericRange({
          min: 1,
          max: 100,
          message: AppStrings.MUST_BE_BETWEEN_1_AND_100,
        }),
      ]}
    >
      <Input
        suffixIcon={<span className={styles.adornment}>%</span>}
        placeholder="0"
        inputMode="numeric"
        data-testid={pctTestId}
      />
    </Form.Item>
  </div>
  );
};

type RevShareConditionsProps = {
  revenueSource?: RevenueSource;
};

// Rows follow the chosen source — the payload mapper nulls the fields of
// a non-selected product, so hidden rows never gate nor reach the wire.
const RevShareConditions: FC<RevShareConditionsProps> = ({
  revenueSource,
}) => {
  return (
    <div className={styles.container}>
      <Form.Item
        name={["revShare", "revenueSource"]}
        label={`${AppStrings.REVENUE_SOURCE}*`}
        rules={[{ required: true, message: AppStrings.REQUIRED }]}
        className={styles.sourceItem}
      >
        <Select
          placeholder={AppStrings.SELECT}
          options={
            REVENUE_SOURCE_OPTIONS as unknown as {
              value: string;
              label: string;
            }[]
          }
          data-testid="new-affiliate-payment-rev-share-source"
        />
      </Form.Item>

      {showsSportsbook(revenueSource) && (
        <ProfitRow
          scope="sportsbook"
          profitLabel={AppStrings.AFFILIATE_PAYMENT_MIN_SPORTSBOOK_REVENUE}
          profitTestId="new-affiliate-payment-rev-share-sportsbook-profit"
          pctTestId="new-affiliate-payment-rev-share-sportsbook-pct"
        />
      )}

      {showsCasino(revenueSource) && (
        <ProfitRow
          scope="casino"
          profitLabel={AppStrings.AFFILIATE_PAYMENT_MIN_CASINO_REVENUE}
          profitTestId="new-affiliate-payment-rev-share-casino-profit"
          pctTestId="new-affiliate-payment-rev-share-casino-pct"
        />
      )}
    </div>
  );
};

export default RevShareConditions;
